<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import Select     from 'primevue/select'
import DatePicker from 'primevue/datepicker'
import Chip       from 'primevue/chip'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'

const emit = defineEmits(['filters-change'])

const router = useRouter()
const { t } = useI18n()
const { fmtDateShort } = useFormatDate()

const feedItems = ref([])
const loading   = ref(false)
const error     = ref('')
const total     = ref(0)
let   lastQuery = {}

// ── Filter state ──────────────────────────────────────────────────────────────
const creatorFilter  = ref(null)
const countryFilter  = ref(null)
const dateRange      = ref(null)

// ── Filter options ────────────────────────────────────────────────────────────
const creatorOptions = ref([])
const countryOptions = ref([])
const optionsLoading = ref(false)

async function loadFilterOptions() {
  optionsLoading.value = true
  try {
    const { data } = await api.getRssFeedItemFilterOptions()
    creatorOptions.value = (data.creators || []).map(c => ({ label: `${c.value} (${c.count})`, value: c.value }))
    countryOptions.value = (data.countries || []).map(c => ({ label: c.value, value: c.value }))
  } catch (e) {
    console.error('News filter options error:', e)
  } finally {
    optionsLoading.value = false
  }
}

// ── Active filter chips ───────────────────────────────────────────────────────
const activeChips = computed(() => {
  const chips = []
  if (creatorFilter.value) chips.push({ key: 'creator', label: `${t('news.list.filterSourceLabel')}: ${creatorFilter.value}` })
  if (countryFilter.value) chips.push({ key: 'country', label: `${t('news.list.filterCountryLabel')}: ${countryFilter.value}` })
  if (dateRange.value?.[0]) {
    const from = fmtDate(dateRange.value[0])
    const to   = dateRange.value[1] ? fmtDate(dateRange.value[1]) : '…'
    chips.push({ key: 'date', label: `${t('news.list.filterDateLabel')}: ${from} – ${to}` })
  }
  return chips
})

const hasAnyFilter = computed(() => activeChips.value.length > 0)

function removeChip(key) {
  if (key === 'creator') creatorFilter.value = null
  if (key === 'country') countryFilter.value = null
  if (key === 'date')    dateRange.value     = null
  onFilterChange()
}

function resetFilters() {
  creatorFilter.value = null
  countryFilter.value = null
  dateRange.value     = null
  onFilterChange()
}

// ── Columns ───────────────────────────────────────────────────────────────────
const columns = [
  { field: 'pub_date',     header: t('news.list.colDate'),    sortable: true,  default: true,  style: 'width: 9rem' },
  { field: 'country_code', header: t('news.list.colCountry'), sortable: true,  default: true,  style: 'width: 5rem' },
  { field: 'creator',      header: t('news.list.colSource'),  sortable: true,  default: true },
  { field: 'title',        header: t('news.list.colTitle'),   sortable: true,  default: true },
]

const fmtDate = (d) => fmtDateShort(d, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

function toISODate(d) {
  if (!d) return undefined
  try { return d instanceof Date ? d.toISOString() : new Date(d).toISOString() } catch { return undefined }
}

async function load(q = {}) {
  lastQuery = q
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.getRssFeedItems({
      limit:        q.limit  ?? 25,
      page:         q.page   ?? 1,
      q:            q.search || undefined,
      creator:      creatorFilter.value  || undefined,
      country_code: countryFilter.value  || undefined,
      dateFrom:     toISODate(dateRange.value?.[0]),
      dateTo:       toISODate(dateRange.value?.[1]),
    })
    if (data?.items) {
      feedItems.value = data.items
      total.value = data.total ?? data.items.length
    } else if (Array.isArray(data)) {
      feedItems.value = data
      total.value = data.length
    } else {
      feedItems.value = []
      total.value = 0
    }
  } catch (err) {
    console.error('Error fetching feed items:', err)
    error.value = t('news.list.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadFilterOptions()
  load()
})

function onQueryChange(q) { load(q) }
function onFilterChange()  { load({ ...lastQuery, page: 1 }) }

watch([countryFilter, creatorFilter], () => {
  emit('filters-change', {
    country: countryFilter.value ?? null,
    source:  creatorFilter.value ?? null,
  })
})

function applyExternalFilter({ dimension, value }) {
  if (dimension === 'country') {
    countryFilter.value = countryFilter.value === value ? null : value
  } else if (dimension === 'source') {
    creatorFilter.value = creatorFilter.value === value ? null : value
  } else {
    return
  }
  onFilterChange()
}

defineExpose({ applyExternalFilter })

function countryTooltip(row) {
  const parts = [row.city, row.region, row.country_code].filter(Boolean)
  return parts.join(' · ')
}

function onFlagError(e) {
  const img = e.target
  img.style.display = 'none'
  const fallback = img.parentElement?.querySelector('.flag-fallback')
  if (fallback) fallback.classList.add('flag-fallback-visible')
}
</script>

<template>
  <div class="news-overview">

    <!-- ── Filter-Card ─────────────────────────────────────────────────────── -->
    <div class="hw-card filter-card">
      <div class="hw-card-header">
        <i class="pi pi-filter hw-card-icon" />
        <span class="hw-card-title">{{ t('news.list.filtersTitle') }}</span>
        <pButton
          v-if="hasAnyFilter"
          icon="pi pi-times"
          :label="t('news.list.filterReset')"
          size="small"
          severity="secondary"
          text
          @click="resetFilters"
        />
      </div>

      <div class="filter-grid">
        <!-- Quelle / Creator -->
        <div class="filter-field">
          <label>{{ t('news.list.filterSourceLabel') }}</label>
          <Select
            v-model="creatorFilter"
            :options="creatorOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('news.list.filterSourcePlaceholder')"
            :loading="optionsLoading"
            filter
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>

        <!-- Land -->
        <div class="filter-field">
          <label>{{ t('news.list.filterCountryLabel') }}</label>
          <Select
            v-model="countryFilter"
            :options="countryOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('news.list.filterCountryPlaceholder')"
            :loading="optionsLoading"
            filter
            show-clear
            class="w-full"
            @change="onFilterChange"
          />
        </div>

        <!-- Zeitraum -->
        <div class="filter-field">
          <label>{{ t('news.list.filterDateLabel') }}</label>
          <DatePicker
            v-model="dateRange"
            selection-mode="range"
            :placeholder="t('news.list.filterDatePlaceholder')"
            date-format="dd.mm.yy"
            show-button-bar
            show-icon
            class="w-full"
            @update:model-value="onFilterChange"
          />
        </div>
      </div>

      <div v-if="hasAnyFilter" class="filter-chips">
        <Chip
          v-for="chip in activeChips"
          :key="chip.key"
          :label="chip.label"
          removable
          @remove="removeChip(chip.key)"
        />
      </div>
    </div>

    <!-- ── Tabelle ─────────────────────────────────────────────────────────── -->
    <AppTable
      table-key="threat-news-list"
      :columns="columns"
      :data="feedItems"
      :total="total"
      :loading="loading"
      lazy
      row-clickable
      :default-rows="25"
      @refresh="load(lastQuery)"
      @query-change="onQueryChange"
      @row-click="(row) => router.push(`/threat-news/news/${row.id}`)"
    >
      <template #cell-pub_date="{ row }">
        <span class="date-cell">{{ fmtDate(row.pub_date) }}</span>
      </template>

      <template #cell-country_code="{ row }">
        <span v-if="row.country_code" class="flag-cell" v-tooltip.top="countryTooltip(row)">
          <img
            :src="`/assets/flags/svg/${row.country_code.toLowerCase()}.svg`"
            :alt="row.country_code"
            class="flag-icon"
            @error="onFlagError"
          />
          <span class="flag-fallback">{{ row.country_code }}</span>
        </span>
        <span v-else class="opacity-40">—</span>
      </template>

      <template #cell-creator="{ row }">
        <span class="creator-cell">{{ row.creator || '—' }}</span>
      </template>

      <template #cell-title="{ row }">
        <span class="title-cell">
          {{ row.title || '—' }}
          <a
            v-if="row.link"
            :href="row.link"
            target="_blank"
            rel="noopener"
            v-tooltip.top="t('news.list.openTooltip')"
            class="hw-link-icon"
            @click.stop
          >
            <i class="pi pi-external-link" />
          </a>
        </span>
      </template>
    </AppTable>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>

<style scoped>
.news-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.date-cell    { white-space: nowrap; font-size: .85rem; opacity: .8; }
.creator-cell { font-size: .82rem; opacity: .75; }
.title-cell   { cursor: pointer; font-weight: 500; }
.title-cell:hover { text-decoration: underline; color: var(--p-primary-color); }

.flag-cell { display: inline-flex; align-items: center; gap: .35rem; }
.flag-icon {
  width: 22px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px var(--p-content-border-color);
}
.flag-fallback { display: none; font-family: ui-monospace, monospace; font-size: .75rem; font-weight: 600; opacity: .75; }
.flag-fallback-visible { display: inline; }

.hw-link-icon {
  display: inline-flex;
  align-items: center;
  padding: 4px 6px;
  color: var(--p-text-color);
  text-decoration: none;
  font-size: 0.85rem;
}
.hw-link-icon:hover { color: var(--p-primary-color); }

/* ── Filter-Card ────────────────────────────────────────────────────────────── */
.filter-card { padding-bottom: 12px; }

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 14px;
  padding: 8px 14px 4px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-field label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-color-secondary);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 14px 0;
}
.filter-chips :deep(.p-chip) {
  font-size: 0.78rem;
  padding: 2px 8px;
}
</style>
